/**
 * tinyMarkdown.js — v2.5.132
 * ─────────────────────────────────────────────────────────────────
 * A minimal markdown → React tree renderer. Supports the subset
 * that the toolbar in MarkdownEditor produces:
 *   ## Heading (level 2)
 *   **bold** / __bold__
 *   _italic_ / *italic*
 *   [link text](https://url)
 *   paragraphs separated by blank lines
 *
 * Deliberately NOT supporting: tables, fenced code blocks, images,
 * lists, raw HTML. The toolbar doesn't produce them and the
 * public-page surface is a marketing landing page where these
 * extras add risk (XSS via raw HTML, malformed lists, oversized
 * images breaking the layout) for negligible value.
 *
 * Security:
 *   • URL scheme validation: only http: / https: / mailto: / relative URLs are
 *     rendered as anchors. javascript: / data: / vbscript: are dropped.
 *   • All text emits as React children (escaped automatically by
 *     React). No dangerouslySetInnerHTML anywhere.
 *
 * If we later add `marked` + `DOMPurify` as a real dep, this file
 * is the deletion target.
 */

import { Fragment } from 'react';

// Inline pattern matchers, in priority order. Each returns either
// { node, rest } or null. `rest` is the unconsumed tail.
function matchBold(text) {
  // **bold** or __bold__. Greedy on the inner content; the close
  // marker must be a matching pair. No nesting of bold-in-bold.
  const m = text.match(/^(\*\*|__)([^]+?)\1/);
  if (!m) return null;
  return { node: <strong>{renderInline(m[2])}</strong>, rest: text.slice(m[0].length) };
}

function matchItalic(text) {
  // *italic* or _italic_. Must not match the start of a **bold**;
  // call AFTER matchBold.
  const m = text.match(/^([*_])([^]+?)\1/);
  if (!m) return null;
  return { node: <em>{renderInline(m[2])}</em>, rest: text.slice(m[0].length) };
}

function matchLink(text) {
  // [text](url). URL scheme is whitelisted; everything else gets
  // rendered as plain text (showing the user the intent, not
  // creating a clickable danger).
  const m = text.match(/^\[([^\]]+)\]\(([^)]+)\)/);
  if (!m) return null;
  const url = m[2].trim();
  const isSafe = (
    /^https?:\/\//i.test(url) ||
    /^mailto:/i.test(url) ||
    url.startsWith('/') ||
    url.startsWith('#')
  );
  if (!isSafe) {
    // Render as plain text — preserves the manager's content
    // without the clickable hazard.
    return { node: <Fragment>{m[0]}</Fragment>, rest: text.slice(m[0].length) };
  }
  return {
    node: (
      <a href={url} target={url.startsWith('http') ? '_blank' : undefined}
         rel={url.startsWith('http') ? 'noopener noreferrer' : undefined}>
        {m[1]}
      </a>
    ),
    rest: text.slice(m[0].length),
  };
}

// Render inline content (no block elements). Returns an array of
// React nodes interspersed with plain strings.
function renderInline(text) {
  const out = [];
  let rest = text;
  let buf = '';
  // Each loop: try every pattern at the current head. If one
  // matches, flush the text buffer and emit the matched node.
  // If none match, eat one character and continue.
  let keyCounter = 0;
  while (rest.length > 0) {
    const matchers = [matchBold, matchItalic, matchLink];
    let matched = null;
    for (const fn of matchers) {
      const result = fn(rest);
      if (result) { matched = result; break; }
    }
    if (matched) {
      if (buf) { out.push(buf); buf = ''; }
      out.push(<Fragment key={`m${keyCounter++}`}>{matched.node}</Fragment>);
      rest = matched.rest;
    } else {
      buf += rest.charAt(0);
      rest = rest.slice(1);
    }
  }
  if (buf) out.push(buf);
  return out;
}

/**
 * Render a markdown string to a React fragment. Splits on blank
 * lines to find blocks; each block is either a heading (## ) or
 * a paragraph. Empty input renders as null.
 */
export function renderMarkdown(src) {
  if (!src || typeof src !== 'string') return null;
  const trimmed = src.trim();
  if (!trimmed) return null;

  // Split into blocks on blank lines. Each block is one paragraph
  // or heading.
  const blocks = trimmed.split(/\n{2,}/);
  return (
    <Fragment>
      {blocks.map((block, idx) => {
        const lines = block.split('\n');
        const first = lines[0];
        if (first.startsWith('## ')) {
          // Heading. Remaining lines (rare) treated as paragraph.
          return (
            <Fragment key={idx}>
              <h2 style={{ fontSize: 22, fontWeight: 700, margin: '24px 0 12px' }}>
                {renderInline(first.slice(3))}
              </h2>
              {lines.length > 1 && (
                <p style={{ margin: '0 0 12px', lineHeight: 1.6 }}>
                  {renderInline(lines.slice(1).join(' '))}
                </p>
              )}
            </Fragment>
          );
        }
        // Plain paragraph. Single-line breaks inside a paragraph
        // become spaces (markdown convention).
        return (
          <p key={idx} style={{ margin: '0 0 12px', lineHeight: 1.6 }}>
            {renderInline(lines.join(' '))}
          </p>
        );
      })}
    </Fragment>
  );
}
